// src/platform/components/DatasetsTable.jsx

function titleCase(str = "") {
    return str
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
}

function datasetName(ds) {
    return `${titleCase(ds.basin)} ${titleCase(ds.region)} ${ds.acquisition_date}`;
}


// ── WARM COVERAGE PILL ───────────────────────────────────────────────────────
//
// This column used to render <StatusPill status={ds.status} />. api_datasets has
// no `status` column -- the warm status is `warm_status` -- so it received
// undefined on every row and fell through to StatusPill's `unknown` grey. It has
// been static since it was written, which is exactly how it looked.
//
// COVERAGE IS warmed_tiles + already_warm_tiles, NOT warmed_tiles.
// warmed_tiles counts only what the LAST RUN stored, and re-warming an
// already-warm partition legitimately stores nothing: both live datasets read
// warmed_tiles 0 with full coverage in already_warm_tiles right now. A pill off
// warmed_tiles would show 0/262 -- red -- at the moment coverage is perfect.
//
// AND IT IS A CLAIM WITH AN AGE, NOT LIVE CACHE STATE. nginx expires tiles at 30
// days and evicts under max_size, so a warm from six weeks ago describes a cache
// that no longer exists. Past the expiry window the pill stops asserting green
// and says `stale` instead, because the honest answer is "unknown" rather than a
// number nobody re-measured.
const CACHE_TTL_DAYS = 30;

function WarmPill({ ds, live }) {
    // 10px -> 12px. Lands on Tailwind's text-xs step, which the rest of this
    // table already uses, rather than another arbitrary value.
    const base = "px-2 py-0.5 text-xs rounded border tabular-nums";
    const GREY = "bg-slate-500/15 text-slate-400 border-slate-500/30";
    const GREEN = "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    const AMBER = "bg-amber-500/15 text-amber-400 border-amber-500/30";
    const RED = "bg-red-500/15 text-red-400 border-red-500/30";
    const CYAN = "bg-cyan-500/15 text-cyan-400 border-cyan-500/30";

    // A job running right now outranks the stored row, which is a snapshot from
    // whenever the last one finished.
    if (live && (live.phase === "starting" || live.phase === "running")) {
        const label = live.phase === "starting" || live.total == null
            ? "warming…"
            : `${live.attempted}/${live.total}`;
        return <span className={`${base} ${CYAN}`}>{label}</span>;
    }

    const total = ds.total_tiles ?? 0;
    const covered = (ds.warmed_tiles ?? 0) + (ds.already_warm_tiles ?? 0);
    const incomplete = ds.incomplete_tiles ?? 0;

    if (!ds.warm_status) {
        return <span className={`${base} ${GREY}`} title="no warm job has ever run for this dataset">never warmed</span>;
    }
    if (ds.warm_status === "failed" || ds.warm_status === "cancelled") {
        return <span className={`${base} ${RED}`} title={`last warm ${ds.warm_status}`}>{ds.warm_status}</span>;
    }
    // Zero tiles is a REAL result, not an empty one: a dataset wholly superseded
    // by a newer collect is never latest-with-coverage anywhere, so there is
    // nothing to warm. Neutral, not green and not red -- it is neither warm nor
    // a problem.
    if (total === 0) {
        return <span className={`${base} ${GREY}`} title="nothing to warm — superseded by a newer collect at every point">n/a</span>;
    }

    const ageDays = ds.finished_at
        ? (Date.now() - new Date(ds.finished_at).getTime()) / 86400000
        : null;
    const stale = ageDays != null && ageDays > CACHE_TTL_DAYS;

    const pct = covered / total;
    const tone = stale ? GREY : pct >= 0.98 ? GREEN : pct >= 0.5 ? AMBER : RED;

    const title =
        `${covered}/${total} tiles warm at z${ds.z_gate ?? "?"}`
        + (incomplete ? ` · ${incomplete} incomplete (rendered, not cached)` : "")
        + (ageDays != null ? ` · warmed ${ageDays < 1 ? "today" : `${Math.floor(ageDays)}d ago`}` : "")
        + (stale ? ` · past the ${CACHE_TTL_DAYS}d cache expiry, so coverage is unverified` : "");

    return (
        <span className={`${base} ${tone}`} title={title}>
            {stale ? "stale" : `${covered}/${total}`}
        </span>
    );
}

export function DatasetsTable({ datasets, warming, onWarm }) {
    return (
        <div className="rounded-lg border border-bg-border overflow-hidden">
            <table className="w-full text-xs border-collapse">
                <thead className="bg-bg-soft/60 text-xs border-b border-bg-border">
                    <tr>
                        <th className="text-left px-3 py-2 text-slate-500">Dataset</th>
                        <th className="text-left px-3 py-2 text-slate-500">Basin</th>
                        <th className="text-left px-3 py-2 text-slate-500">Region</th>
                        <th className="text-left px-3 py-2 text-slate-500">Date</th>
                        <th className="text-left px-3 py-2 text-slate-500">Status</th>
                        <th className="text-right px-3 py-2 text-slate-500">Actions</th>
                    </tr>
                </thead>

                <tbody>
                    {datasets.map(ds => (
                        <tr
                            key={ds.dataset_key}
                            className="border-b border-bg-border hover:bg-bg-soft/40 transition"
                        >
                            <td className="px-3 py-1 font-medium">
                                {datasetName(ds)}
                                <div className="text-[10px] text-muted font-mono">
                                    {ds.dataset_key}
                                </div>
                            </td>

                            <td className="px-3 py-1 text-muted">
                                {ds.basin}
                            </td>

                            <td className="px-3 py-1 text-muted">
                                {ds.region}
                            </td>

                            <td className="px-3 py-1 text-muted">
                                {ds.acquisition_date}
                            </td>

                            <td className="px-3 py-1">
                                <WarmPill ds={ds} live={warming?.[ds.dataset_key]} />
                            </td>

                            <td className="px-3 py-1 text-right">
                                {(() => {
                                    // `warming[key]` is a job-progress object, not a boolean.
                                    // The states are deliberately distinct: a dataset that is
                                    // wholly superseded by a newer collect warms ZERO tiles,
                                    // and that is a correct answer rather than a failure. A
                                    // label that said only "Warming…" then "Warm" could not
                                    // tell that apart from a job that did nothing.
                                    const w = warming?.[ds.dataset_key];
                                    const busy = w && (w.phase === "starting" || w.phase === "running");

                                    let label = "Warm";
                                    let tone = "border-cyan-400/50 text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20";
                                    let title;

                                    if (w?.phase === "starting") {
                                        label = "Starting…";
                                        tone = "border-slate-600 text-slate-500 cursor-not-allowed";
                                    } else if (w?.phase === "running") {
                                        // total is null only until the first progress row lands.
                                        label = w.total == null
                                            ? `${w.attempted}…`
                                            : `${w.attempted}/${w.total}`;
                                        tone = "border-cyan-400/40 text-cyan-200 bg-cyan-400/5 cursor-not-allowed";
                                    } else if (w?.phase === "done") {
                                        label = w.total === 0
                                            ? "0 tiles"
                                            : `${w.total}/${w.total} warm`;
                                        tone = "border-emerald-400/50 text-emerald-300 bg-emerald-400/10";
                                        // The breakdown belongs somewhere a user can reach, but
                                        // not in the label: stored vs already-warm vs incomplete
                                        // is the difference between a real warm and a no-op.
                                        title = w.total === 0
                                            ? (w.message || "nothing to warm")
                                            : `stored ${w.stored} · already warm ${w.alreadyWarm}`
                                              + (w.incomplete ? ` · ${w.incomplete} incomplete (not cached)` : "")
                                              + (w.message ? `\n${w.message}` : "");
                                    } else if (w?.phase === "error") {
                                        label = "Failed";
                                        tone = "border-rose-400/50 text-rose-300 bg-rose-400/10";
                                        title = w.message || "warm failed";
                                    }

                                    return (
                                        <button
                                            disabled={busy}
                                            title={title}
                                            onClick={() => onWarm(ds)}
                                            className={`text-xs px-3 py-1.5 rounded-md border transition tabular-nums ${tone}`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })()}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}